import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearOrganizationContext, getOrganizationContext } from '../orgContext';
import config from '../config';
import './AdminLayout.css';
import { useAuth0 } from '@auth0/auth0-react';
import { adminNavigationSections, filterNavSections, getMembershipRoles, getUserRoles, mergeRoles } from '../navigationConfig';
import AdminSidebarNavigation from './AdminSidebarNavigation';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, user, logout } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [organizationDisplayName, setOrganizationDisplayName] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [membershipRoles, setMembershipRoles] = useState<string[]>([]);
  const [platformRoles, setPlatformRoles] = useState<string[]>([]);
  const organizationContext = getOrganizationContext();
  const userRoles = useMemo(() => mergeRoles(getUserRoles(user), membershipRoles, platformRoles), [membershipRoles, platformRoles, user]);
  const visibleSections = useMemo(() => filterNavSections(adminNavigationSections, isAuthenticated, userRoles), [isAuthenticated, userRoles]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    const context = getOrganizationContext();
    const shouldRequireOrganization =
      !location.pathname.startsWith('/manage-categories-v2') &&
      !(location.pathname === '/manage-schedule' || location.pathname.startsWith('/manage-schedule/'));

    if (shouldRequireOrganization && !context?.organizationId) {
      navigate('/analytics', { replace: true });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const loadOrganizationDisplayName = async () => {
      if (!isAuthenticated || !organizationContext?.organizationId) {
        setOrganizationDisplayName('No organization');
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Unable to load organization details');
        }

        const organizations = await response.json();
        if (Array.isArray(organizations)) {
          const matchedOrganization = organizations.find((item: any) =>
            Number(item.organizationId) === organizationContext.organizationId ||
            String(item.organizationSlug || '').toLowerCase() === organizationContext.organizationSlug.toLowerCase()
          );

          const resolvedName = matchedOrganization?.organizationName || matchedOrganization?.name || matchedOrganization?.displayName || organizationContext.organizationSlug || 'No organization';
          setOrganizationDisplayName(String(resolvedName));
          return;
        }
      } catch (error) {
        console.warn('Unable to load organization display name', error);
      }

      setOrganizationDisplayName(organizationContext.organizationSlug || 'No organization');
    };

    void loadOrganizationDisplayName();
  }, [getAccessTokenSilently, isAuthenticated, organizationContext?.organizationId, organizationContext?.organizationSlug]);

  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMembershipRoles([]);
      setPlatformRoles([]);
      return;
    }

    let cancelled = false;

    const loadMembershipRoles = async () => {
      try {
        const token = await getAccessTokenSilently();

        const membershipsResponse = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (membershipsResponse.ok) {
          const organizations = await membershipsResponse.json();
          if (!cancelled) {
            setMembershipRoles(getMembershipRoles(organizations));
          }
        }

        const platformRolesResponse = await fetch(`${config.apiBaseUrl}/api/organizations/platform-user-roles`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!platformRolesResponse.ok) {
          if (!cancelled) {
            setPlatformRoles([]);
          }
          return;
        }

        const platformRolesData = await platformRolesResponse.json();
        if (!cancelled && Array.isArray(platformRolesData)) {
          const resolvedPlatformRoles = platformRolesData
            .filter((role: any) => role && typeof role === 'object')
            .map((role: any) => {
              const rawRoleKey = role.roleKey || role.RoleKey || role.role || role.name;
              return typeof rawRoleKey === 'string' && rawRoleKey.trim().length > 0 ? rawRoleKey.trim() : '';
            })
            .filter((role: string) => role.length > 0)
            .map((role: string) => role.toLowerCase());

          setPlatformRoles(resolvedPlatformRoles);
        }
      } catch (error) {
        console.warn('Unable to load organization membership roles for admin layout', error);
      }
    };

    void loadMembershipRoles();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setUserDisplayName('');
      return;
    }

    const resolvedUserName =
      user.name ||
      user.nickname ||
      user.email ||
      'Signed in user';

    setUserDisplayName(String(resolvedUserName));
  }, [isAuthenticated, user]);

  const normalizedPath = useMemo(() => location.pathname.toLowerCase(), [location.pathname]);

  const isLinkActive = (to: string) => {
    const normalizedTo = to.toLowerCase();
    if (normalizedPath === normalizedTo) {
      return true;
    }

    return normalizedPath.startsWith(`${normalizedTo}/`);
  };

  return (
    <div className="admin-shell">
      {isMobile && (
        <header className="admin-mobile-topbar">
          <button
            type="button"
            className="admin-mobile-menu-btn"
            onClick={() => setSidebarOpen(prev => !prev)}
            aria-label="Toggle admin navigation"
          >
            {sidebarOpen ? 'Close' : 'Menu'}
          </button>
          <div className="admin-mobile-title">Admin Console</div>
          <Link to="/" className="admin-mobile-back-link">Main Site</Link>
        </header>
      )}

      <div
        className={`admin-sidebar-backdrop ${sidebarOpen ? 'is-open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside className={`admin-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-title">Admin Console</div>
          <div className="admin-sidebar-subtitle">Unified management workspace</div>
          <div style={{ marginTop: 8, fontSize: '0.84rem', fontWeight: 700, color: '#dce8ff', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ opacity: 0.8 }}>Org:</span>
              <span>{organizationDisplayName || 'Loading...'}</span>
            </div>
            {userDisplayName && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#e8f1ff' }}>
                <span style={{ opacity: 0.8 }}>User:</span>
                <span>{userDisplayName}</span>
              </div>
            )}
          </div>
          <Link to="/" className="admin-back-link">Back to main site</Link>
        </div>

        <AdminSidebarNavigation
          visibleSections={visibleSections}
          isLinkActive={isLinkActive}
          isMobile={isMobile}
          onNavigate={() => setSidebarOpen(false)}
        />

        {isAuthenticated && (
          <div className="admin-sidebar-footer" style={{ padding: '16px 16px 20px' }}>
            <button
              type="button"
              onClick={() => {
                setSidebarOpen(false);
                clearOrganizationContext();
                logout({ logoutParams: { returnTo: window.location.origin } });
              }}
              style={{
                width: '100%',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 14px',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 700,
                textAlign: 'left',
              }}
            >
              Logout
            </button>
          </div>
        )}
      </aside>

      <main className="admin-main">
        <div className="admin-content">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
